import React, { useEffect } from 'react';
import { BlogPost } from '../types';

interface BlogListProps {
  posts: BlogPost[];
  onReadPost: (postId: string) => void;
  onBack: () => void;
}

export const BlogList: React.FC<BlogListProps> = ({ posts, onReadPost, onBack }) => {
  
  // SEO: Update title when viewing blog list
  useEffect(() => {
    document.title = "Relationship Advice & Blog - LoveOnRead";
  }, []);

  return (
    <div className="bg-slate-50 min-h-full font-sans">
      
      <nav className="bg-white border-b border-slate-100 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 cursor-pointer" onClick={onBack}>
                <img 
                    src="https://image2url.com/r2/default/images/1769296557239-bc506436-f738-4598-80fa-95e27487a870.png" 
                    alt="LoveOnRead Logo" 
                    className="w-8 h-8 object-contain"
                />
                <span className="font-bold text-xl tracking-tight">
                    <span className="text-love">Love</span>
                    <span className="text-read">OnRead</span>
                    <span className="text-slate-400 font-medium ml-2 text-sm">Blog</span>
                </span>
            </div>
            <button 
                onClick={onBack}
                className="text-sm font-medium text-slate-600 hover:text-slate-900"
            >
                Back to Home
            </button>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center mb-16">
            <h1 className="text-4xl font-extrabold text-slate-900 mb-4">Relationship Advice & Tips</h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Practical guides to improve your communication, deepen intimacy, and navigate conflict.
            </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
                <article 
                    key={post.id} 
                    className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-all cursor-pointer flex flex-col h-full group"
                    onClick={() => onReadPost(post.id)}
                >
                    <div className="h-48 overflow-hidden relative">
                        <img 
                            src={post.imageUrl} 
                            alt={post.title} 
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-slate-800 uppercase tracking-wide">
                            {post.category}
                        </div>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                        <div className="flex items-center gap-2 text-xs text-slate-400 mb-3">
                            <span>{post.date}</span>
                            <span>•</span>
                            <span>{post.readTime}</span>
                        </div>
                        <h2 className="text-xl font-bold text-slate-900 mb-3 leading-tight group-hover:text-brand-600 transition-colors">
                            {post.title}
                        </h2>
                        <p className="text-slate-500 text-sm leading-relaxed mb-4 flex-1">
                            {post.excerpt}
                        </p>
                        <span className="text-brand-600 font-bold text-sm flex items-center gap-1 mt-auto">
                            Read Article 
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                            </svg>
                        </span>
                    </div>
                </article>
            ))}
        </div>
      </main>

      <footer className="bg-white border-t border-slate-100 py-12 px-6 mt-12">
        <div className="max-w-xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Ready to practice what you learned?</h3>
            <p className="text-slate-500 mb-8">
                LoveOnRead helps you simulate these exact conversations so you are ready for the real thing.
            </p>
            <button 
                onClick={onBack}
                className="bg-brand-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-brand-700 transition-colors shadow-lg shadow-brand-500/30"
            >
                Start Free Trial
            </button>
        </div>
      </footer>
    </div>
  );
};